import { useState, useEffect, useCallback } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStoredValue((prev) => {
      const newValue = value instanceof Function ? value(prev) : value;
      try {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      } catch {
        // storage full or unavailable
      }
      return newValue;
    });
  }, [key]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key]);

  return [storedValue, setValue];
}

export function downloadFile(filename: string, content: string, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

export function getAppUrl(): string {
  const envUrl = import.meta.env.VITE_APP_URL;
  if (envUrl) return envUrl.replace(/\/$/, '');
  const origin = window.location.origin;
  if (origin.includes('webcontainer.io') || origin.includes('stackblitz.io')) {
    return 'https://www.aurelianmarketingengine.com';
  }
  return origin;
}

export function asUuidOrNull(value: string | null | undefined): string | null {
  return value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    ? value
    : null;
}

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

export function printContent(title: string, content: string) {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) return;
  const isHtml = content.trim().startsWith('<') || content.trim().startsWith('<!DOCTYPE');
  const escapedContent = content.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html><head><title>${title}</title>
    <style>
      body { font-family: Georgia, 'Times New Roman', serif; line-height: 1.6; color: #1e293b; max-width: 800px; margin: 0 auto; padding: 40px; }
      h1, h2, h3 { color: #0f172a; margin-top: 1.5em; }
      pre { white-space: pre-wrap; word-wrap: break-word; font-family: 'Courier New', monospace; font-size: 12px; }
      img { max-width: 100%; height: auto; display: block; margin: 20px 0; }
      @media print { body { padding: 0; } }
    </style></head>
    <body>${isHtml ? content : `<pre>${escapedContent}</pre>`}</body></html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { printWindow.print(); }, 500);
}

export async function downloadAsPDF(title: string, content: string) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const maxWidth = pageWidth - margin * 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(title, margin, margin + 20, { maxWidth });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  const isHtml = content.trim().startsWith('<') || content.trim().startsWith('<!DOCTYPE');

  // Check if content contains an image tag
  const imgMatch = content.match(/<img[^>]*src="([^"]*)"[^>]*>/i);
  if (imgMatch) {
    const imgSrc = imgMatch[1];
    try {
      const imgData = await fetchImageAsBase64(imgSrc);
      if (imgData) {
        const imgEl = await new Promise<HTMLImageElement>((resolve, reject) => {
          const i = new Image();
          i.crossOrigin = 'anonymous';
          i.onload = () => resolve(i);
          i.onerror = () => reject(new Error('load failed'));
          i.src = imgData;
        });

        const imgW = imgEl.naturalWidth;
        const imgH = imgEl.naturalHeight;
        const maxImgWidth = maxWidth;
        const maxImgHeight = pageHeight - margin * 2 - 60;
        let renderW = maxImgWidth;
        let renderH = (imgH / imgW) * renderW;
        if (renderH > maxImgHeight) {
          renderH = maxImgHeight;
          renderW = (imgW / imgH) * renderH;
        }

        const format = imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(imgData, format, margin, margin + 50, renderW, renderH);
      }
    } catch {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('Image could not be embedded in this PDF.', margin, margin + 50, { maxWidth });
    }
  } else {
    const plainText = isHtml
      ? content.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim()
      : content;

    const lines = doc.splitTextToSize(plainText, maxWidth) as string[];
    let y = margin + 50;
    const lineHeight = 16;

    for (const line of lines) {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  doc.save(`${title}.pdf`);
}

async function fetchImageAsBase64(url: string): Promise<string | null> {
  // Already a data URL
  if (url.startsWith('data:')) return url;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export type DownloadFormat = 'txt' | 'html' | 'md' | 'json';

export function downloadContentAsFormat(filename: string, content: string, format: DownloadFormat) {
  const mimeTypes: Record<DownloadFormat, string> = {
    txt: 'text/plain',
    html: 'text/html',
    md: 'text/markdown',
    json: 'application/json',
  };
  const ext = format === 'json' && !content.trim().startsWith('{') ? 'txt' : format;
  let body = content;
  if (format === 'html' && !content.trim().startsWith('<')) {
    body = `<!DOCTYPE html>\n<html><head><meta charset="utf-8"><title>${filename}</title></head>\n<body>\n<pre>${content.replace(/</g, '&lt;')}</pre>\n</body></html>`;
  } else if (format === 'json' && !content.trim().startsWith('{')) {
    body = JSON.stringify({ title: filename, content }, null, 2);
  }
  downloadFile(`${filename}.${ext}`, body, mimeTypes[format]);
}

export async function cropImageToSize(imageUrl: string, targetW: number, targetH: number): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Failed to load image for cropping'));
    i.src = imageUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');

  const sourceAspect = img.width / img.height;
  const targetAspect = targetW / targetH;

  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (sourceAspect > targetAspect) {
    sh = img.height;
    sw = img.height * targetAspect;
    sx = (img.width - sw) / 2;
  } else {
    sw = img.width;
    sh = img.width / targetAspect;
    sy = (img.height - sh) / 2;
  }

  sx = Math.max(0, Math.min(sx, img.width - sw));
  sy = Math.max(0, Math.min(sy, img.height - sh));

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
      'image/png'
    );
  });
}

interface BannerTextOptions {
  headline: string;
  subtext: string;
  cta: string;
  websiteUrl?: string;
  fieldLevel: 'minimal' | 'standard' | 'full';
}

function drawWebsiteUrl(ctx: CanvasRenderingContext2D, width: number, height: number, url: string): void {
  if (!url.trim()) return;
  const padding = Math.max(8, Math.round(Math.min(width, height) * 0.08));
  const fontSize = Math.max(7, Math.round(Math.min(width, height) * 0.035));
  const text = url.trim().replace(/^https?:\/\//, '');
  ctx.save();
  ctx.font = `600 ${fontSize}px Inter, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
  ctx.shadowBlur = Math.max(1, Math.round(fontSize * 0.3));
  ctx.shadowOffsetY = 0;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillText(text, width / 2, height - padding * 0.55, width - padding * 2);
  ctx.restore();
}

export async function overlayWebsiteOnImage(imageUrl: string, websiteUrl: string): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Failed to load image for URL overlay'));
    i.src = imageUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');
  ctx.drawImage(img, 0, 0);
  drawWebsiteUrl(ctx, img.width, img.height, websiteUrl);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
  });
}

export async function renderBannerText(blob: Blob, width: number, height: number, options: BannerTextOptions): Promise<Blob> {
  const imageUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Failed to load banner for text rendering'));
      element.src = imageUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas context unavailable');
    context.drawImage(image, 0, 0, width, height);

    const padding = Math.max(8, Math.round(Math.min(width, height) * 0.08));
    const isVertical = height > width * 2;
    const isCompact = width <= 320 && height <= 250;
    context.textBaseline = 'alphabetic';

    if (isVertical) {
      const textWidth = Math.max(1, width - padding * 2);
      const wrapText = (text: string, font: string): string[] => {
        context.font = font;
        return text.trim().split(/\\s+/).reduce<string[]>((lines, word) => {
          const current = lines[lines.length - 1] ?? '';
          const candidate = current ? `${current} ${word}` : word;
          if (current && context.measureText(candidate).width > textWidth) lines.push(word);
          else lines[lines.length - 1] = candidate;
          return lines;
        }, ['']).filter(Boolean);
      };

      const headlineFontSize = Math.max(14, Math.min(26, Math.round(width * 0.15)));
      const headlineLineHeight = Math.round(headlineFontSize * 1.08);
      const headlineFont = `700 ${headlineFontSize}px Arial, sans-serif`;
      const headlineLines = wrapText(options.headline, headlineFont);
      let cursorY = padding + headlineFontSize;
      context.textAlign = 'center';
      context.shadowColor = 'rgba(0, 0, 0, 0.7)';
      context.shadowBlur = Math.max(2, Math.round(width * 0.025));
      context.shadowOffsetY = Math.max(1, Math.round(width * 0.01));
      context.fillStyle = '#ffffff';
      context.font = headlineFont;
      headlineLines.forEach((line) => {
        context.fillText(line, width / 2, cursorY, textWidth);
        cursorY += headlineLineHeight;
      });

      if (options.fieldLevel !== 'minimal' && options.subtext.trim()) {
        const subtextFontSize = Math.max(10, Math.min(15, Math.round(width * 0.085)));
        const subtextFont = `400 ${subtextFontSize}px Arial, sans-serif`;
        const subtextLines = wrapText(options.subtext, subtextFont).slice(0, 4);
        cursorY += Math.round(subtextFontSize * 0.45);
        context.font = subtextFont;
        subtextLines.forEach((line) => {
          context.fillText(line, width / 2, cursorY, textWidth);
          cursorY += Math.round(subtextFontSize * 1.2);
        });
      }

      if (options.cta.trim()) {
        const buttonWidth = textWidth;
        const buttonHeight = Math.max(32, Math.min(52, Math.round(width * 0.3)));
        const buttonY = Math.min(height - padding - buttonHeight, cursorY + Math.round(width * 0.12));
        context.shadowColor = 'rgba(0, 0, 0, 0.25)';
        context.shadowBlur = Math.max(2, Math.round(width * 0.02));
        context.shadowOffsetY = 0;
        context.fillStyle = '#f59e0b';
        context.beginPath();
        context.roundRect(padding, buttonY, buttonWidth, buttonHeight, Math.min(10, Math.round(buttonHeight * 0.25)));
        context.fill();
        context.shadowColor = 'transparent';
        context.fillStyle = '#172033';
        const ctaFontSize = Math.max(10, Math.min(16, Math.round(buttonWidth / Math.max(6, options.cta.length * 0.58))));
        context.font = `700 ${ctaFontSize}px Arial, sans-serif`;
        context.fillText(options.cta.trim(), width / 2, buttonY + buttonHeight / 2 + ctaFontSize * 0.35, buttonWidth - 12);
      }
    } else if (isCompact) {
      const textWidth = Math.max(1, width - padding * 2);
      const wrapText = (text: string, font: string, maxLines: number): string[] => {
        context.font = font;
        return text.trim().split(/\\s+/).reduce<string[]>((lines, word) => {
          const current = lines[lines.length - 1] ?? '';
          const candidate = current ? `${current} ${word}` : word;
          if (current && context.measureText(candidate).width > textWidth && lines.length < maxLines) lines.push(word);
          else lines[lines.length - 1] = candidate;
          return lines;
        }, ['']).filter(Boolean);
      };

      const headlineFontSize = Math.max(16, Math.min(30, Math.round(height * 0.15)));
      const headlineFont = `700 ${headlineFontSize}px Arial, sans-serif`;
      const headlineLineHeight = Math.round(headlineFontSize * 1.08);
      const headlineLines = wrapText(options.headline, headlineFont, 2);
      let cursorY = padding + headlineFontSize;
      context.textAlign = 'center';
      context.shadowColor = 'rgba(0, 0, 0, 0.7)';
      context.shadowBlur = Math.max(2, Math.round(height * 0.025));
      context.shadowOffsetY = Math.max(1, Math.round(height * 0.01));
      context.fillStyle = '#ffffff';
      context.font = headlineFont;
      headlineLines.forEach((line) => {
        context.fillText(line, width / 2, cursorY, textWidth);
        cursorY += headlineLineHeight;
      });

      if (options.fieldLevel !== 'minimal' && options.subtext.trim()) {
        const subtextFontSize = Math.max(10, Math.min(15, Math.round(height * 0.06)));
        const subtextFont = `400 ${subtextFontSize}px Arial, sans-serif`;
        const subtextLines = wrapText(options.subtext, subtextFont, 3);
        cursorY += Math.round(subtextFontSize * 0.35);
        context.font = subtextFont;
        subtextLines.forEach((line) => {
          context.fillText(line, width / 2, cursorY, textWidth);
          cursorY += Math.round(subtextFontSize * 1.2);
        });
      }

      if (options.cta.trim()) {
        const buttonWidth = Math.min(Math.round(width * 0.48), textWidth);
        const buttonHeight = Math.max(32, Math.min(44, Math.round(height * 0.18)));
        const buttonX = Math.round((width - buttonWidth) / 2);
        const buttonY = height - padding - buttonHeight;
        context.shadowColor = 'rgba(0, 0, 0, 0.25)';
        context.shadowBlur = Math.max(2, Math.round(height * 0.02));
        context.shadowOffsetY = 0;
        context.fillStyle = '#f59e0b';
        context.beginPath();
        context.roundRect(buttonX, buttonY, buttonWidth, buttonHeight, Math.min(10, Math.round(buttonHeight * 0.25)));
        context.fill();
        context.shadowColor = 'transparent';
        context.fillStyle = '#172033';
        const ctaFontSize = Math.max(10, Math.min(16, Math.round(buttonWidth / Math.max(6, options.cta.length * 0.58))));
        context.font = `700 ${ctaFontSize}px Arial, sans-serif`;
        context.fillText(options.cta.trim(), width / 2, buttonY + buttonHeight / 2 + ctaFontSize * 0.35, buttonWidth - 12);
      }
    } else {
      const isWide = width > height * 2.5;
      const ctaWidth = options.cta ? Math.min(Math.round(width * 0.22), Math.max(72, width - padding * 2)) : 0;

      if (isWide) {
        const textWidth = Math.max(1, width - padding * 2 - (ctaWidth ? ctaWidth + padding : 0));
        const headlineFontSize = Math.max(14, Math.min(Math.round(height * 0.4), Math.round(textWidth / Math.max(8, options.headline.length * 0.56))));
        const headlineLineHeight = Math.round(headlineFontSize * 1.08);
        const headlineFont = `700 ${headlineFontSize}px Arial, sans-serif`;
        context.font = headlineFont;
        const headlineLines = options.headline.trim().split(/\\s+/).reduce<string[]>((lines, word) => {
          const current = lines[lines.length - 1] ?? '';
          const candidate = current ? `${current} ${word}` : word;
          if (current && context.measureText(candidate).width > textWidth && lines.length < 2) lines.push(word);
          else lines[lines.length - 1] = candidate;
          return lines;
        }, ['']).filter(Boolean);

        context.textAlign = 'center';
        context.shadowColor = 'rgba(0, 0, 0, 0.7)';
        context.shadowBlur = Math.max(2, Math.round(height * 0.025));
        context.shadowOffsetY = Math.max(1, Math.round(height * 0.01));
        context.fillStyle = '#ffffff';
        context.font = headlineFont;
        const headlineBlockH = headlineLineHeight * headlineLines.length;
        const titleY = padding + headlineFontSize;
        const textCenterX = ctaWidth ? (width - ctaWidth - padding) / 2 : width / 2;
        headlineLines.forEach((line, index) => context.fillText(line, textCenterX, titleY + index * headlineLineHeight, textWidth));

        if (options.fieldLevel !== 'minimal' && options.subtext.trim()) {
          const subtextFontSize = Math.max(10, Math.min(Math.round(height * 0.16), Math.round(textWidth / Math.max(12, options.subtext.length * 0.55))));
          const subtextFont = `400 ${subtextFontSize}px Arial, sans-serif`;
          context.font = subtextFont;
          context.fillText(options.subtext.trim(), textCenterX, height - padding - subtextFontSize * 0.2, textWidth);
        }

        if (options.cta.trim()) {
          const buttonX = width - padding - ctaWidth;
          const buttonHeight = Math.max(28, Math.min(Math.round(height * 0.4), 52));
          const buttonY = Math.round((height - buttonHeight) / 2);
          context.shadowColor = 'rgba(0, 0, 0, 0.25)';
          context.shadowBlur = Math.max(2, Math.round(height * 0.02));
          context.shadowOffsetY = 0;
          context.fillStyle = '#f59e0b';
          context.beginPath();
          context.roundRect(buttonX, buttonY, ctaWidth, buttonHeight, Math.min(10, Math.round(buttonHeight * 0.25)));
          context.fill();
          context.shadowColor = 'transparent';
          context.fillStyle = '#172033';
          const ctaFontSize = Math.max(10, Math.min(Math.round(buttonHeight * 0.38), Math.round(ctaWidth / Math.max(6, options.cta.length * 0.58))));
          context.font = `700 ${ctaFontSize}px Arial, sans-serif`;
          context.fillText(options.cta.trim(), buttonX + ctaWidth / 2, buttonY + buttonHeight / 2 + ctaFontSize * 0.35, ctaWidth - 12);
        }
      } else {
        const textWidth = Math.max(1, width - padding * 2 - (ctaWidth ? ctaWidth + padding : 0));
        const headlineFontSize = Math.max(12, Math.min(Math.round(height * 0.34), Math.round(textWidth / Math.max(8, options.headline.length * 0.56))));
        const headlineLineHeight = Math.round(headlineFontSize * 1.08);
        const headlineLines = options.headline.trim().split(/\\s+/).reduce<string[]>((lines, word) => {
          const current = lines[lines.length - 1] ?? '';
          const candidate = current ? `${current} ${word}` : word;
          context.font = `700 ${headlineFontSize}px Arial, sans-serif`;
          if (current && context.measureText(candidate).width > textWidth && lines.length < 2) lines.push(word);
          else lines[lines.length - 1] = candidate;
          return lines;
        }, ['']);

        const textX = padding;
        const titleY = Math.max(padding + headlineFontSize, (height - headlineLineHeight * headlineLines.length) / 2 + headlineFontSize * 0.75);
        context.textAlign = 'left';
        context.shadowColor = 'rgba(0, 0, 0, 0.7)';
        context.shadowBlur = Math.max(2, Math.round(height * 0.025));
        context.shadowOffsetY = Math.max(1, Math.round(height * 0.01));
        context.fillStyle = '#ffffff';
        context.font = `700 ${headlineFontSize}px Arial, sans-serif`;
        headlineLines.forEach((line, index) => context.fillText(line, textX, titleY + index * headlineLineHeight, textWidth));

        if (options.fieldLevel !== 'minimal' && options.subtext.trim()) {
          const subtextFontSize = Math.max(10, Math.min(Math.round(height * 0.14), Math.round(textWidth / Math.max(12, options.subtext.length * 0.55))));
          context.font = `400 ${subtextFontSize}px Arial, sans-serif`;
          context.fillText(options.subtext.trim(), textX, Math.min(height - padding, titleY + headlineLines.length * headlineLineHeight + subtextFontSize * 1.35), textWidth);
        }

        if (options.cta.trim()) {
          const buttonX = width - padding - ctaWidth;
          const buttonHeight = Math.max(24, Math.min(Math.round(height * 0.34), 48));
          const buttonY = Math.round((height - buttonHeight) / 2);
          context.shadowColor = 'rgba(0, 0, 0, 0.25)';
          context.shadowBlur = Math.max(2, Math.round(height * 0.02));
          context.shadowOffsetY = 0;
          context.fillStyle = '#f59e0b';
          context.beginPath();
          context.roundRect(buttonX, buttonY, ctaWidth, buttonHeight, Math.min(10, Math.round(buttonHeight * 0.25)));
          context.fill();
          context.shadowColor = 'transparent';
          context.fillStyle = '#172033';
          const ctaFontSize = Math.max(10, Math.min(Math.round(buttonHeight * 0.38), Math.round(ctaWidth / Math.max(6, options.cta.length * 0.58))));
          context.font = `700 ${ctaFontSize}px Arial, sans-serif`;
          context.textAlign = 'center';
          context.fillText(options.cta.trim(), buttonX + ctaWidth / 2, buttonY + buttonHeight / 2 + ctaFontSize * 0.35, ctaWidth - 12);
        }
      }
    }

    drawWebsiteUrl(context, width, height, options.websiteUrl ?? '');

    return new Promise((resolve, reject) => {
      canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Banner text rendering failed')), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

export interface BrandOverlayOptions {
  headline: string;
  subtext: string;
  cta: string;
  copyright: string;
  websiteUrl?: string;
  targetW: number;
  targetH: number;
  fieldLevel: 'minimal' | 'standard' | 'full';
}

export async function brandUploadedImage(imageUrl: string, options: BrandOverlayOptions): Promise<Blob> {
  const croppedBlob = await cropImageToSize(imageUrl, options.targetW, options.targetH);
  let brandedBlob = croppedBlob;
  if (options.headline.trim()) {
    brandedBlob = await renderBannerText(croppedBlob, options.targetW, options.targetH, {
      headline: options.headline.trim(),
      subtext: options.subtext.trim(),
      cta: options.cta.trim(),
      websiteUrl: options.websiteUrl,
      fieldLevel: options.fieldLevel,
    });
  }
  if (!options.copyright.trim() && !options.websiteUrl?.trim()) return brandedBlob;

  const brandedUrl = URL.createObjectURL(brandedBlob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load branded image for copyright overlay'));
      el.src = brandedUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = options.targetW;
    canvas.height = options.targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.drawImage(image, 0, 0, options.targetW, options.targetH);

    const fontSize = Math.max(7, Math.round(Math.min(options.targetW, options.targetH) * 0.035));
    ctx.font = `400 ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = Math.max(1, Math.round(fontSize * 0.3));
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    const padding = Math.max(4, Math.round(Math.min(options.targetW, options.targetH) * 0.03));
    if (options.copyright.trim()) {
      ctx.fillText(`© ${options.copyright.trim()}`, options.targetW - padding, options.targetH - padding);
    }

    drawWebsiteUrl(ctx, options.targetW, options.targetH, options.websiteUrl ?? '');

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Copyright overlay failed'))), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(brandedUrl);
  }
}

export async function readFileAsText(file: File): Promise<string> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return extractPdfText(file);
  if (extension === 'docx') {
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
    return result.value;
  }

  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

  return extension === 'rtf' ? text
    .replace(/\\'[0-9a-fA-F]{2}/g, match => String.fromCharCode(parseInt(match.slice(2), 16)))
    .replace(/\\(?:par|line|tab)\b/g, match => match.includes('tab') ? '\\t' : '\\n')
    .replace(/\\[a-zA-Z]+-?\d*\s?/g, '')
    .replace(/[{}]/g, '')
    .replace(/\\([\\{}])/g, '$1')
    .replace(/\s+\n/g, '\\n')
    .trim() : text;
}

export async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const version = (pdfjs as any).version;
  (pdfjs as any).GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.mjs`;
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = (pdfjs as any).getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const lines: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: any) => item.str)
      .join(' ');
    lines.push(pageText);
  }
  return lines.join('\n\n');
}
